/**
 * @module engine/sim/geometry
 * @summary The machine: elements on a screw, pins in a barrel, and the axial mesh.
 * @description
 *   A browser port of `sim/jcccompound/elements.py` and `geometry.py`, which
 *   remain the reference implementation and carry the full rationale. The two
 *   are held together by shared fixture cases (see `selftest/sim.ts`), because
 *   the same run must give the same numbers wherever it is computed.
 *
 *   The one idea worth repeating here: a 1D simulator is bookkeeping over the
 *   screw, and the element at each station decides the local physics. A
 *   conveying element is drag-limited and part-full at ambient pressure; a
 *   restriction ring is full by construction and builds pressure; a kneading
 *   flight over a pin row is where the shear and the mixing happen. Get the
 *   sequence wrong and every profile is wrong with it.
 *
 *   And the co-kneader-specific part: an element carries the BARREL opposite
 *   it, not just the screw. The flight is interrupted and the mixing comes
 *   from it sweeping past stationary pins, so pulling pins out of a barrel
 *   section changes the physics though the screw never moved.
 *
 *   The DATA shapes live in `src/types.ts` with the rest of the document
 *   contract, because a screw configuration saves with the drawing. This
 *   module holds only the behaviour that reads them.
 */
import type { BarrelStation, ElementKind, ElementSpec, Machine, SimProvenance } from '../../types';

export type { BarrelStation, ElementKind, ElementSpec, Machine, SimProvenance };

/** Human labels, for the configuration editor. */
export const ELEMENT_LABELS: Readonly<Record<ElementKind, string>> = {
  feed: 'Feed',
  conveying: 'Conveying',
  kneading: 'Kneading',
  restriction: 'Restriction',
  reverse: 'Reverse (seal)',
  mixing: 'Distributive mixing',
  side_feed: 'Side feed',
  vent: 'Vent',
  discharge: 'Discharge',
};

/** One line of help per element, shown in the editor so the choice is informed. */
export const ELEMENT_HELP: Readonly<Record<ElementKind, string>> = {
  feed: 'Under the main throat. Solids, not melt — bulk density and friction rule here.',
  conveying: 'Forward transport. Drag-limited and part-full at ambient pressure.',
  kneading: 'Interrupted flight sweeping the barrel pins. The mixing workhorse.',
  restriction: 'Restriction ring. Runs full, builds pressure, forces dispersion.',
  reverse: 'Reverse pitch. A melt seal — ahead of a vent, or to stop a side feed blowing back.',
  mixing: 'Fine distributive element. Many flow splits, much less shear stress.',
  side_feed: 'Under a side stuffer. Conveying, with room left for the incoming filler.',
  vent: 'Under the vacuum port. Must stay part-full or it floods the vent line.',
  discharge: 'Hands over to the discharge unit. Builds no die pressure itself.',
};

/** Elements that run 100% full by construction rather than by rate. */
export function isFullyFilled(kind: ElementKind): boolean {
  return kind === 'restriction' || kind === 'reverse';
}

/** Elements that do mixing work worth counting. */
export function isMixingKind(kind: ElementKind): boolean {
  return kind === 'kneading' || kind === 'mixing' || kind === 'restriction';
}

/** Elements that transport forward under drag. */
export function conveys(kind: ElementKind): boolean {
  return !isFullyFilled(kind);
}

export function defaultBarrel(pinRows = 3): BarrelStation {
  return { pinRows, pinsPerRow: 3, pinDiameterMm: 12, flightPinGapMm: 2.5, heated: true };
}

/**
 * A machine and its screw, as the solver consumes it.
 *
 * Structurally the document's `ScrewConfig` minus the bookkeeping — id, line
 * code, recorded operating points — so a saved configuration can be handed
 * straight to `simulate` without conversion.
 */
export interface ScrewConfiguration {
  machine: Machine;
  elements: ElementSpec[];
}

/** A validation problem, addressed to whoever is editing the configuration. */
export interface ConfigProblem {
  where: string;
  message: string;
  severity: 'error' | 'warning';
}

// ── element construction ────────────────────────────────────────────────

export function makeElement(kind: ElementKind, lengthD: number, over: Partial<ElementSpec> = {}): ElementSpec {
  const bare = kind === 'feed' || kind === 'conveying' || kind === 'side_feed' || kind === 'vent';
  const base: ElementSpec = {
    kind,
    lengthD,
    name: ELEMENT_LABELS[kind],
    pitchD: kind === 'reverse' ? -0.8 : kind === 'feed' || kind === 'side_feed' || kind === 'vent' ? 1.5 : 1.0,
    flights: 3,
    flightClearanceMm: 0.35,
    freeVolumeFrac: bare ? 0.7 : 0.5,
    restrictionRatio: kind === 'restriction' ? 0.45 : 1.0,
    barrel: bare ? { ...defaultBarrel(0), pinsPerRow: 0 } : defaultBarrel(3),
    provenance: 'default',
  };
  return { ...base, ...over };
}

// ── kinematics ──────────────────────────────────────────────────────────

/** Rotational speed at the flight tip, m/s. */
export function tipSpeedMs(m: Machine, rpm: number): number {
  return (Math.PI * m.diameterMm * 1e-3 * rpm) / 60;
}

/** Mean axial speed of the oscillation, m/s — both halves of the stroke count. */
export function axialSpeedMs(m: Machine, rpm: number): number {
  return (2 * m.strokeMm * 1e-3 * m.strokesPerRev * rpm) / 60;
}

/**
 * The speed that actually sets shear rate in the gaps, m/s.
 *
 * Rotation and oscillation are perpendicular, so they add in quadrature. This
 * is the co-kneader correction — a twin-screw model stops at the rotational
 * term and reads the gap shear low.
 */
export function resultantSpeedMs(m: Machine, rpm: number): number {
  return Math.hypot(tipSpeedMs(m, rpm), axialSpeedMs(m, rpm));
}

/** Torque the drive can deliver at this speed, respecting constant power. */
export function availableTorqueNm(m: Machine, rpm: number): number {
  return rpm <= m.baseSpeedRpm ? m.ratedTorqueNm : (m.ratedTorqueNm * m.baseSpeedRpm) / rpm;
}

export function annulusAreaMm2(m: Machine): number {
  return (Math.PI / 4) * (m.diameterMm ** 2 - m.rootDiameterMm ** 2);
}

// ── element geometry ────────────────────────────────────────────────────

export function elementLengthMm(e: ElementSpec, diameterMm: number): number {
  return e.lengthD * diameterMm;
}

export function freeAreaMm2(e: ElementSpec, m: Machine): number {
  return annulusAreaMm2(m) * e.freeVolumeFrac * e.restrictionRatio;
}

export function freeVolumeMm3(e: ElementSpec, m: Machine): number {
  return freeAreaMm2(e, m) * elementLengthMm(e, m.diameterMm);
}

/**
 * Flow splits this element makes per revolution.
 *
 * Each flight segment meets each pin row once per turn. It does NOT scale with
 * machine size — a 200 mm machine still has 3 or 4 rows — which is why scaling
 * a kneader on volume alone overstates the mixing you will get.
 */
export function mixingEventsPerRev(e: ElementSpec): number {
  return isMixingKind(e.kind) ? e.flights * e.barrel.pinRows : 0;
}

// ── configuration ───────────────────────────────────────────────────────

export function totalLengthD(c: ScrewConfiguration): number {
  return c.elements.reduce((n, e) => n + e.lengthD, 0);
}

export function totalLengthMm(c: ScrewConfiguration): number {
  return totalLengthD(c) * c.machine.diameterMm;
}

export function freeVolumeCm3(c: ScrewConfiguration): number {
  return c.elements.reduce((n, e) => n + freeVolumeMm3(e, c.machine), 0) / 1000;
}

export function mixingEventsTotal(c: ScrewConfiguration): number {
  return c.elements.reduce((n, e) => n + mixingEventsPerRev(e), 0);
}

export function zoneLengthsD(c: ScrewConfiguration): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of c.elements) out[e.kind] = (out[e.kind] ?? 0) + e.lengthD;
  return out;
}

/** One cell of the axial mesh: where it is, and what it is inside. */
export interface Station {
  zMm: number;
  dzMm: number;
  element: ElementSpec;
  elementIndex: number;
  /** Which barrel module — and so which TCU loop — this cell sits under. */
  sectionIndex: number;
}

/**
 * Where the barrel modules start and end, in mm from the throat.
 *
 * Sections are declared with their own lengths because a screw fills its
 * barrel but the two subdivisions do not align: a 4 D module boundary lands
 * mid-element as often as not.
 */
export function sectionBoundariesMm(c: ScrewConfiguration): number[] {
  const d = c.machine.diameterMm;
  const out: number[] = [];
  let z = 0;
  for (const s of c.machine.barrelSections ?? []) {
    z += s.lengthD * d;
    out.push(z);
  }
  return out;
}

/**
 * Divide the screw into cells, never letting one straddle an element boundary.
 *
 * A boundary is a discontinuity in the physics — fill jumps, pressure starts
 * building — so a cell spanning one would average away the thing the model
 * exists to resolve. Each element gets a whole number of cells, and short
 * elements like a restriction ring still get at least two.
 */
export function mesh(c: ScrewConfiguration, targetDzMm = 2.5): Station[] {
  if (!(targetDzMm > 0)) throw new Error('mesh: targetDzMm must be positive');
  const d = c.machine.diameterMm;

  // Split on the UNION of both subdivisions. An element boundary is a
  // discontinuity in the flow physics; a barrel-section boundary is a
  // discontinuity in the thermal boundary condition. A cell that straddles
  // either averages away something the model exists to resolve.
  const elementEdges: number[] = [];
  let z = 0;
  for (const e of c.elements) {
    z += elementLengthMm(e, d);
    elementEdges.push(z);
  }
  const total = z;
  const sectionEdges = sectionBoundariesMm(c).filter((s) => s > 1e-9 && s < total - 1e-9);

  // Snap a section edge that lands within a fraction of a cell of an element
  // edge onto it, or the union generates a degenerate sub-millimetre cell
  // whose fill and viscosity are numerical noise.
  const snap = Math.min(1, 0.4 * targetDzMm);
  const edges = [0];
  for (const s of sectionEdges) {
    const near = elementEdges.find((e) => Math.abs(e - s) <= snap);
    edges.push(near ?? s);
  }
  edges.push(...elementEdges);
  const cuts = [...new Set(edges.map((v) => Math.round(v * 1e6) / 1e6))].sort((a, b) => a - b);

  const out: Station[] = [];
  for (let k = 0; k < cuts.length - 1; k++) {
    const from = cuts[k];
    const to = cuts[k + 1];
    const span = to - from;
    if (span <= 1e-9) continue;
    const mid = (from + to) / 2;
    const elementIndex = elementEdges.findIndex((e) => mid < e + 1e-9);
    const sectionIndex = sectionIndexAt(c, mid);
    const n = Math.max(2, Math.round(span / targetDzMm));
    const dz = span / n;
    for (let i = 0; i < n; i++) {
      out.push({
        zMm: from + dz * (i + 0.5),
        dzMm: dz,
        element: c.elements[Math.max(0, elementIndex)],
        elementIndex: Math.max(0, elementIndex),
        sectionIndex,
      });
    }
  }
  return out;
}

/** Which barrel module covers a position. -1 when no sections are declared. */
export function sectionIndexAt(c: ScrewConfiguration, zMm: number): number {
  const edges = sectionBoundariesMm(c);
  if (!edges.length) return -1;
  for (let i = 0; i < edges.length; i++) if (zMm < edges[i] + 1e-9) return i;
  return edges.length - 1; // past the last declared module — clamp rather than drop
}

/** Everything still resting on a placeholder rather than machine data. */
export function unmeasured(c: ScrewConfiguration): string[] {
  const gaps: string[] = [];
  if (c.machine.provenance !== 'measured') {
    gaps.push(
      `machine ${c.machine.name}: diameter, stroke and torque are ${c.machine.provenance}, not off the machine card`,
    );
  }
  c.elements.forEach((e, i) => {
    if (e.provenance !== 'measured') {
      gaps.push(
        `element ${i + 1} (${e.name}, ${e.lengthD}D): ${e.provenance} geometry — free volume ${e.freeVolumeFrac}, clearance ${e.flightClearanceMm} mm, pin gap ${e.barrel.flightPinGapMm} mm`,
      );
    }
  });
  return gaps;
}

export function isMeasured(c: ScrewConfiguration): boolean {
  return unmeasured(c).length === 0;
}

/**
 * Check a configuration before it is run.
 *
 * Errors stop the run; warnings are things that are legal but probably not
 * what was meant — a machine with no kneading, a vent with nothing sealing it.
 */
export function validate(c: ScrewConfiguration): ConfigProblem[] {
  const p: ConfigProblem[] = [];
  const m = c.machine;

  if (!(m.diameterMm > 0)) p.push({ where: 'machine', message: 'screw diameter must be positive', severity: 'error' });
  if (m.rootDiameterMm >= m.diameterMm) {
    p.push({ where: 'machine', message: 'root diameter must be smaller than the bore', severity: 'error' });
  }
  if (!(m.strokeMm > 0)) p.push({ where: 'machine', message: 'stroke must be positive', severity: 'error' });
  if (m.strokeMm / m.diameterMm > 0.4) {
    p.push({
      where: 'machine',
      message: `stroke is ${(m.strokeMm / m.diameterMm).toFixed(2)} D — co-kneaders are usually 0.15-0.25 D. Check the units`,
      severity: 'warning',
    });
  }
  if (!c.elements.length) {
    p.push({ where: 'screw', message: 'a configuration needs at least one element', severity: 'error' });
    return p;
  }

  c.elements.forEach((e, i) => {
    const at = `element ${i + 1} (${e.name})`;
    if (!(e.lengthD > 0)) p.push({ where: at, message: 'length must be positive', severity: 'error' });
    if (e.flights < 1) p.push({ where: at, message: 'needs at least one flight', severity: 'error' });
    if (!(e.freeVolumeFrac > 0 && e.freeVolumeFrac <= 1)) {
      p.push({ where: at, message: 'free volume fraction must be between 0 and 1', severity: 'error' });
    }
    if (!(e.restrictionRatio > 0 && e.restrictionRatio <= 1)) {
      p.push({ where: at, message: 'restriction ratio must be between 0 and 1', severity: 'error' });
    }
    if (e.kind === 'reverse' && e.pitchD >= 0) {
      p.push({ where: at, message: 'a reverse element needs a negative pitch', severity: 'error' });
    }
    if (e.kind !== 'reverse' && e.pitchD < 0) {
      p.push({
        where: at,
        message: 'negative pitch — set the kind to Reverse (seal) so the solver fills it',
        severity: 'error',
      });
    }
    if (isMixingKind(e.kind) && e.barrel.pinRows === 0) {
      p.push({
        where: at,
        message: 'a mixing element with no pin rows does no mixing — is the pin schedule right?',
        severity: 'warning',
      });
    }
    if (e.barrel.pinRows > 0 && !(e.barrel.flightPinGapMm > 0)) {
      p.push({ where: at, message: 'flight-to-pin gap must be positive', severity: 'error' });
    }
  });

  // ── barrel modules ──
  const sections = c.machine.barrelSections ?? [];
  if (!sections.length) {
    p.push({
      where: 'barrel',
      message: 'no barrel sections defined — temperature has nowhere to be set',
      severity: 'error',
    });
  } else {
    sections.forEach((s, i) => {
      if (!(s.lengthD > 0)) {
        p.push({ where: `barrel section ${i + 1}`, message: 'length must be positive', severity: 'error' });
      }
    });
    const barrelD = sections.reduce((n, s) => n + s.lengthD, 0);
    const screwD = c.elements.reduce((n, e) => n + e.lengthD, 0);
    if (Math.abs(barrelD - screwD) > 0.1) {
      p.push({
        where: 'barrel',
        message:
          `the barrel sections total ${barrelD.toFixed(1)} D but the screw is ${screwD.toFixed(1)} D — ` +
          'a screw fills its barrel, so one of the two is mistyped',
        severity: 'warning',
      });
    }
  }

  if (c.machine.screwTempMode === undefined) {
    p.push({
      where: 'machine',
      message: 'screw temperature control not stated — assuming none leaves the root adiabatic, which ' +
        'OVERSTATES peak melt temperature rather than being neutral',
      severity: 'warning',
    });
  }

  const total = totalLengthD(c);
  if (total < 8) {
    p.push({ where: 'screw', message: `${total.toFixed(1)} L/D is very short for a co-kneader (11-25 typical)`, severity: 'warning' });
  }
  if (total > 40) {
    p.push({ where: 'screw', message: `${total.toFixed(1)} L/D is very long — check the element lengths`, severity: 'warning' });
  }
  if (mixingEventsTotal(c) === 0) {
    p.push({ where: 'screw', message: 'no mixing elements with pins — this machine would not compound', severity: 'warning' });
  }

  const kinds = c.elements.map((e) => e.kind);
  const vent = kinds.indexOf('vent');
  if (vent >= 0 && !kinds.slice(0, vent).includes('reverse') && !kinds.slice(0, vent).includes('restriction')) {
    p.push({
      where: 'screw',
      message: 'the vent has no seal upstream — melt will back up into the vacuum line',
      severity: 'warning',
    });
  }
  const side = kinds.indexOf('side_feed');
  if (side >= 0 && !kinds.slice(0, side).includes('reverse')) {
    p.push({
      where: 'screw',
      message: 'a side feed with no upstream seal can blow back through the throat',
      severity: 'warning',
    });
  }
  return p;
}
